import { NextSeo } from "next-seo"
import { useRouter } from "next/router"

import image, { CATEGORY_TREASURE } from "lacinetek-common/src/images"
import { metadata, FEATURE_BONUS_TYPE } from "lacinetek-common/src/metadata"

import { useTranslation } from "lib/i18n/translation"
import { useCountry } from "lib/country"
import { i18n } from "lib/i18n"
import { FETCH_TREASURE } from "lib/graphql/queries"
import Gtm from "lib/gtm"
import { BONUS_ARCHIVES, BONUS_EXCLUSIFS } from "lib/tools/product"
import { getPageCanonicalUrl } from "lib/tools/urls"
import matchLocale from "lib/redirects/locale-matcher"

import { Query } from "components/common"
import Products from "components/common/products"
import { Bonuses } from "components/common"
import BonusesQuery from "components/common/bonuses-query"
import {
	Nav,
	Header,
	Edito,
	Corpus,
	Films
} from "components/treasure"
import {
	baseProduct,
	baseTreasure,
	baseBonus
} from "components/common/skeleton"

const baseBonuses = [baseBonus, baseBonus, baseBonus, baseBonus]
const baseProducts = [baseProduct, baseProduct, baseProduct, baseProduct]

const Treasure = ({ slug }) => {
	const { c } = useTranslation("common")
	const { asPath } = useCountry()
	const router = useRouter()
	const url = router ? router.asPath : null
	const canonical = getPageCanonicalUrl(router, asPath)

	const RenderLoading = () => <>
		<Nav key="loading-nav" />
		<Header
			key="loading-header"
			product={baseProduct}
			treasure={baseTreasure}
			loading={true}
		/>
		<Edito
			key="loading-edito"
			treasure={baseTreasure}
			loading={true}
		/>
		<Corpus
			key="loading-corpus"
			treasure={baseTreasure}
			corpus={baseProducts}
			loading={true}
		/>
		<Films
			key="loading-films"
			treasure={baseTreasure}
			products={[...baseProducts, ...baseProducts, ...baseProducts]}
			loading={true}
		/>
		<Bonuses
			key="loading-bonus-exclu"
			type={BONUS_EXCLUSIFS}
			bonuses={baseBonuses}
			loading={true}
		/>
		<Bonuses
			key="loading-bonus-archives"
			type={BONUS_ARCHIVES}
			bonuses={baseBonuses}
			loading={true}
		/>
	</>

	return (
		<Query
			query={FETCH_TREASURE}
			options={{ variables: { query: `linkRewrite.value:"${slug}"` } }}
			renderLoading={() => <RenderLoading />}
		>
			{({ cms: { categories: { items } } }) => {
				const treasure = items.find(i => i.linkRewrite == slug)

				if (!treasure) {
					return <RenderLoading />
				}

				return (
					<>
						<Gtm pageType="Trésor caché" pagePath={url} pageTitle={treasure.metaTitle || treasure.name} />
						<NextSeo
							title={treasure.metaTitle || treasure.name}
							description={treasure.metaDescription || treasure.descriptionShort}
							keywords={treasure.metaKeywords}
							canonical={canonical}
							openGraph={{
								type: "article",
								title: treasure.metaTitle !== "" ? treasure.metaTitle : treasure.name,
								description: treasure.metaDescription !== "" ? treasure.metaDescription : `${c("treasure-of")} ${treasure.name}`,
								url: canonical,
								images: [{ url: image(treasure, CATEGORY_TREASURE) }],
								image: { url: image(treasure, CATEGORY_TREASURE) }
							}}
						/>

						<Nav key="treasures-nav" />
						<Products products={treasure.products.items}>
							{(treasureProducts, loading) => {

								return (
									<>
										<Header
											key="treasure-header"
											product={randomProduct(treasureProducts)}
											treasure={treasure}
											loading={loading}
										/>
										<Edito
											key="treasure-edito"
											treasure={treasure}
											loading={loading}
										/>
										{
											treasure.children.items.length > 0
											&& <Corpus
												key={`treasure-corpus-${treasure.id}${loading ? "-loading" : ""}`}
												treasure={treasure}
												corpus={treasure.children.items}
												loading={loading}
											/>
										}
										<Films
											key="treasure-films"
											treasure={treasure}
											products={treasureProducts}
											loading={loading}
										/>
										<BonusesQuery
											productIds={treasure.products.items.map(a => a.id)}
											renderLoading={() => (
												<>
													<Bonuses
														key="treasure-bonuses-exclu-loading"
														type={BONUS_EXCLUSIFS}
														bonuses={baseBonuses}
														loading={true}
													/>
													<Bonuses
														key="treasure-bonuses-archives-loading"
														type={BONUS_ARCHIVES}
														bonuses={baseBonuses}
														loading={true}
													/>
												</>
											)}
										>
											{
												bonuses => (
													<>
														<Bonuses
															key={`bonus-exclusifs-${treasure.id}-${i18n.language}`}
															type={BONUS_EXCLUSIFS}
															bonuses={bonuses.filter(b => metadata(b, FEATURE_BONUS_TYPE) == BONUS_EXCLUSIFS)}
														/>
														<Bonuses
															key={`bonus-archives-${treasure.id}-${i18n.language}`}
															type={BONUS_ARCHIVES}
															bonuses={bonuses.filter(b => metadata(b, FEATURE_BONUS_TYPE) == BONUS_ARCHIVES)}
														/>
													</>
												)
											}
										</BonusesQuery>
									</>
								)
							}}
						</Products>
					</>
				)
			}}
		</Query>
	)
}

const randomProduct = products => {
	if (products.length) {
		return products[Math.floor(products.length * Math.random())]
	} else {
		return null
	}
}

export const getInitialProps = async (ctx) => {
	const lang = ctx.req?.lng ?? "fr"
	const localeData = await matchLocale(ctx.asPath, ctx.apolloClient)

	return {
		slug: localeData[lang]
	}
}

Treasure.getInitialProps = getInitialProps

export default Treasure
